import { useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import i18n from '@/i18n'
import { useBackgroundTaskStore } from '@/stores/backgroundTaskStore'
import { useNotificationStore } from '@/stores/notificationStore'
import { callApi } from '@/hooks/useFetch'
import { downloadPdfFromApi } from '@/utils/pdfDownload'
import { openNotificationLink } from '@/utils/notificationLink'
import { toast } from '@/lib/toast'
import { toastApiError } from '@/lib/errors'
import { MAX_JOB_POLL_DURATION_MS } from '@/hooks/useGenericJobStatus'
import type { GenericJobStatusResponse } from '@/hooks/useGenericJobStatus'

const POLL_INTERVAL = 2000
const MAX_CONSECUTIVE_FAILURES = 5

export function useBackgroundTaskPoller() {
  const tasks = useBackgroundTaskStore((s) => s.tasks)
  const updateTask = useBackgroundTaskStore((s) => s.updateTask)
  const queryClient = useQueryClient()
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const failureCountsRef = useRef<Record<string, number>>({})

  const activeCount = Object.values(tasks).filter((t) => t.status === 'polling').length

  useEffect(() => {
    if (activeCount === 0) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
      return
    }

    const poll = async () => {
      const currentTasks = useBackgroundTaskStore.getState().tasks
      const active = Object.values(currentTasks).filter((t) => t.status === 'polling')

      await Promise.allSettled(
        active.map(async (task) => {
          try {
            const data = await callApi<GenericJobStatusResponse>(`/jobs/${task.jobId}`)
            failureCountsRef.current[task.id] = 0

            if (data.status === 'completed') {
              updateTask(task.id, {
                status: 'completed',
                progress: 100,
                message: data.message,
              })
              useNotificationStore.getState().triggerShootingStar({
                x: window.innerWidth - 60,
                y: 28,
              })
              const url = task.completedActionUrl
              // Opportunity-creation jobs (generate-from-match/-startup/
              // -discovered) all return {opportunity_id} on completion — the
              // id isn't known until the job actually runs, so this is the
              // only point a link to the specific created opportunity can be
              // built (the enqueue-time notification_url has to fall back to
              // the list for the same reason).
              const opportunityId =
                typeof data.result?.opportunity_id === 'string'
                  ? data.result.opportunity_id
                  : undefined
              toast.notification(task.completedTitle || i18n.t('backgroundTask.completed'), {
                description: task.completedMessage || data.message,
                duration: url || opportunityId ? 8000 : 3000,
                ...(url && {
                  actionLabel: i18n.t('common.download'),
                  onAction: () => {
                    const filename = url.split('/').pop()?.split('?')[0] || 'download.pdf'
                    downloadPdfFromApi(url, filename)
                  },
                }),
                ...(!url &&
                  opportunityId && {
                    actionLabel: i18n.t('common.view'),
                    onAction: () => openNotificationLink(`/opportunities/${opportunityId}`),
                  }),
              })
              if (task.queryKeysToInvalidate) {
                await Promise.allSettled(
                  task.queryKeysToInvalidate.map((key) =>
                    queryClient.invalidateQueries({ queryKey: key })
                  )
                )
              }
            } else if (data.status === 'failed') {
              const error = data.message || i18n.t('backgroundTask.unknownError')
              updateTask(task.id, {
                status: 'failed',
                error,
              })
              toast.error(task.title || i18n.t('backgroundTask.taskFailed'), {
                description: error,
              })
            } else if (Date.now() - task.createdAt > MAX_JOB_POLL_DURATION_MS) {
              const lostMessage = i18n.t('backgroundTask.taskLost')
              updateTask(task.id, {
                status: 'failed',
                error: lostMessage,
              })
              toast.error(task.title || i18n.t('backgroundTask.taskFailed'), {
                description: lostMessage,
              })
            } else {
              updateTask(task.id, {
                progress: data.progress,
                currentItem: data.current_item,
                totalItems: data.total_items,
                message: data.message,
              })
            }
          } catch (err) {
            const failures = (failureCountsRef.current[task.id] || 0) + 1
            failureCountsRef.current[task.id] = failures
            if (failures >= MAX_CONSECUTIVE_FAILURES) {
              failureCountsRef.current[task.id] = 0
              const lostMessage = i18n.t('backgroundTask.taskLost')
              updateTask(task.id, {
                status: 'failed',
                error: lostMessage,
              })
              toastApiError(err, lostMessage)
            }
          }
        })
      )
    }

    poll()
    intervalRef.current = setInterval(poll, POLL_INTERVAL)

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [activeCount, updateTask, queryClient])
}
